import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SIGNS } from '../data/signs';
import { getCompatibility } from '../data/compatibility';

function ScoreBar({ score }: { score: number }) {
  const color = score >= 80 ? '#4ade80' : score >= 60 ? '#F5C842' : score >= 40 ? '#fb923c' : '#f87171';
  return (
    <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', fontWeight: 700, color, marginBottom: '0.25rem' }}>
        {score}<span style={{ fontSize: '1.5rem', color: 'var(--text-muted)' }}>/100</span>
      </div>
      <div style={{ height: '8px', backgroundColor: 'var(--bg-card)', borderRadius: '4px', maxWidth: '300px', margin: '0 auto' }}>
        <div style={{ height: '100%', width: `${score}%`, backgroundColor: color, borderRadius: '4px', transition: 'width 1s ease' }} />
      </div>
    </div>
  );
}

export function CompatibilityPage() {
  const { par } = useParams<{ par: string }>();
  const parts = par?.split('-') ?? [];

  // Find the two signs, handle hyphenated sign names (e.g. "gemeos" is clean but "aries-touro")
  const allSlugs = SIGNS.map(s => s.slug);
  let slug1 = '';
  let slug2 = '';

  for (let i = 1; i < parts.length; i++) {
    const candidate1 = parts.slice(0, i).join('-');
    const candidate2 = parts.slice(i).join('-');
    if (allSlugs.includes(candidate1) && allSlugs.includes(candidate2)) {
      slug1 = candidate1;
      slug2 = candidate2;
      break;
    }
  }

  const sign1 = SIGNS.find(s => s.slug === slug1);
  const sign2 = SIGNS.find(s => s.slug === slug2);
  const compat = getCompatibility(slug1, slug2);

  if (!sign1 || !sign2 || !compat) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)' }}>Combinação não encontrada.</p>
          <Link to="/" style={{ color: 'var(--petrol)', marginTop: '1rem', display: 'inline-block' }}>← Voltar</Link>
        </div>
      </div>
    );
  }

  // Canonical: always sorted alphabetically
  const [canonSlug1, canonSlug2] = [slug1, slug2].sort();
  const canonUrl = `https://professya.com/compatibilidade/${canonSlug1}-${canonSlug2}`;
  const ogImage = `https://professya.com/og/compat-${canonSlug1}-${canonSlug2}.png`;

  return (
    <>
      <Helmet>
        <title>{sign1.name} e {sign2.name}: Compatibilidade Astrológica, Professya</title>
        <meta name="description" content={compat.metaDescription} />
        <meta property="og:title" content={`${sign1.name} e ${sign2.name}: Compatibilidade Astrológica`} />
        <meta property="og:description" content={compat.metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={canonUrl} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${sign1.name} e ${sign2.name}: Compatibilidade Astrológica`,
          description: compat.metaDescription,
          image: ogImage,
          author: { '@type': 'Organization', name: 'Professya', url: 'https://professya.com' },
          publisher: { '@type': 'Organization', name: 'Professya', url: 'https://professya.com' },
          url: canonUrl,
          keywords: `compatibilidade ${sign1.name} ${sign2.name}, sinastria, astrologia`,
          mainEntityOfPage: { '@type': 'WebPage', '@id': canonUrl },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Início', item: 'https://professya.com' },
            { '@type': 'ListItem', position: 2, name: 'Compatibilidade', item: 'https://professya.com/compatibilidade' },
            { '@type': 'ListItem', position: 3, name: `${sign1.name} e ${sign2.name}`, item: canonUrl },
          ],
        })}</script>
      </Helmet>

      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' }}>
        {/* Hero */}
        <div style={{ background: 'linear-gradient(180deg, rgba(245,200,66,0.08) 0%, transparent 100%)', padding: '3rem 1.5rem 2rem', textAlign: 'center' }}>
          <Link to="/" style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', textDecoration: 'none', display: 'inline-block', marginBottom: '1.5rem' }}>
            ✦ Professya
          </Link>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1.5rem', marginBottom: '1rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '3rem' }}>{sign1.emoji}</div>
              <p className="font-display" style={{ fontSize: '1.125rem', margin: '0.25rem 0 0' }}>{sign1.name}</p>
            </div>
            <div style={{ fontSize: '1.5rem', color: 'var(--petrol)' }}>♥</div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '3rem' }}>{sign2.emoji}</div>
              <p className="font-display" style={{ fontSize: '1.125rem', margin: '0.25rem 0 0' }}>{sign2.name}</p>
            </div>
          </div>
          <h1 className="font-display" style={{ fontSize: 'clamp(1.25rem,4vw,1.875rem)', marginBottom: '0.5rem' }}>
            {compat.title}
          </h1>
          <ScoreBar score={compat.score} />
          <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic', maxWidth: '480px', margin: '0 auto' }}>
            {compat.verdict}
          </p>
        </div>

        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 1.5rem 4rem' }}>
          <Section title="A Energia entre os Dois Signos">{compat.intro}</Section>
          <Section title="💘 Amor e Romance">{compat.love}</Section>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <div className="card">
              <h3 className="font-display" style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>✨ Pontos Fortes</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', lineHeight: 1.8 }}>{compat.strengths}</p>
            </div>
            <div className="card">
              <h3 className="font-display" style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>🌀 Desafios</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', lineHeight: 1.8 }}>{compat.challenges}</p>
            </div>
          </div>
          <Section title="💬 Comunicação">{compat.communication}</Section>

          {/* CTA */}
          <div className="card" style={{ textAlign: 'center', border: '1px solid rgba(245,200,66,0.2)', background: 'linear-gradient(135deg, rgba(245,200,66,0.06) 0%, transparent 100%)' }}>
            <p className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
              Quer ir além da compatibilidade solar?
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.625rem' }}>
              A Sinastria do Professya analisa todos os planetas dos dois mapas natais, posições, aspectos e dinâmicas que o signo solar não revela.
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginBottom: '1.25rem' }}>
              Disponível nos planos Mensal e Anual, ou como compra avulsa.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/servicos/sinastria" className="btn btn-primary">✦ Conhecer a Sinastria</Link>
              <Link to="/pricing" className="btn btn-secondary" style={{ fontSize: 'var(--text-label)' }}>Ver planos e preços</Link>
            </div>
          </div>

          {/* Related sign links */}
          <div style={{ marginTop: '2rem' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginBottom: '0.75rem' }}>Explore mais compatibilidades:</p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {sign1.compatible.map(slug => {
                const s = SIGNS.find(x => x.slug === slug);
                if (!s || s.slug === slug2) return null;
                return (
                  <Link key={slug} to={`/compatibilidade/${sign1.slug}-${slug}`}
                    style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', backgroundColor: 'var(--bg-card)', padding: '0.25rem 0.625rem', borderRadius: 'var(--radius-full)', border: '1px solid var(--border-subtle)', textDecoration: 'none' }}>
                    {sign1.name} + {s.name}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: string }) {
  return (
    <section style={{ marginBottom: '2rem' }}>
      <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '0.875rem' }}>{title}</h2>
      <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85 }}>{children}</p>
    </section>
  );
}
